import React, {useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {Button, Col, Container, Dropdown, Row} from 'react-bootstrap';
import {
	ConfirmationDialog
} from "../../Common/ConfirmationDialog";
import {Glyphicon} from "../../Common/GlyphIcon";
import {StreamBankLink} from "../../Common/StreamBankLink";

import {Project} from "../../../Models/StreamBank/Project";

import {ObjectUtils} from "../../../Utils/ObjectUtils";

import {
    usePatchContext,
    useChangeContext,
    useChangeContextAndTool,
} from "../../../Hooks/RouteHooks";
import {
    useEditContextNodeId,
    usePermission,
} from "../../../Hooks/PermissionHooks";
import {useMakePageRequest} from "../../../Hooks/RequestHooks";
import {useShowPermissionDenied} from "../../../Hooks/ErrorHooks";
import {useContextBuilder} from "../../../Hooks/ContextBuilderHooks";

import {useClassNames} from "../../../Hooks/StyleHooks";
import {ChildrenList} from "../../Common/ChildrenList";
import {ContextNodeBenefitSummarySection} from "../../Common/ContextNodeBenefitSummarySection";
import {ProjectModelViewer} from "./ProjectModelViewer";
import {ContactList} from "./ContactList";
import {SbMap} from "../../Common/SbMap";
import {service as ConservationActionService} from '../../../Services/ConservationActionService';
import {service as ConservationActionTypeService} from '../../../Services/ConservationActionTypeService';
import {service as ContactService} from '../../../Services/ContactService';
import {service as ContextNodeService} from '../../../Services/ContextNodeService';
import {service as ProjectService} from '../../../Services/ProjectService';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";


let model = new Project();

export const ProjectsViewPage = props => {
    const context = useSelector(state => state.contextState.context);

    const classNames = useClassNames('sb-projects-view-page');
    const contextBuilder = useContextBuilder(props);
    const makePageRequest = useMakePageRequest();
    const patchContext = usePatchContext();
    const permission = usePermission();
    const showPermissionDenied = useShowPermissionDenied();
    const editContextNodeId = useEditContextNodeId();
    const changeContext = useChangeContext();
    const changeContextAndTool = useChangeContextAndTool();

    const [project, setProject] = useState(null);
    const [existingProject, setExistingProject] = useState(null);
    const [projectValidation, setProjectValidation] = useState([]);
    const [relatedContacts, setRelatedContacts] = useState([]);
    const [childrenConservationActions, setChildrenConservationActions] = useState([]);
    const [conservationActionTypes, setConservationActionTypes] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(false);

    const contextNodeId = project?.contextNodeId;
    const projectId = context.project;


    useEffect(() => {
        if (projectId) {
            loadData();
        }
    }, [projectId]);

    const loadData = () => {
        if(projectId === "new") {
            if (!permission('create_project')) {
                showPermissionDenied();
            } else {
                makePageRequest(
                    () => Promise.all([
                        ConservationActionTypeService.Get(),
                        ContextNodeService.Lookup({
                            programId: context.parentProgram,
                            subProgramId: context.childProgram
                        })
                    ]),
                    ([types, contextNodeLookup]) => {
                        const { program, subProgram } = contextNodeLookup;
                        setConservationActionTypes(types.result);
                        setEditMode(true);
                        setProject({  //TODO: This seems hacky. Our code shouldn't need to know specifics of the model.
                            Location: {     // that should be defined by our model. //Maybe we can have a factory method on it?
                                datumId: 1,
                                instrumentId: 1,
                                unitId: 1
                            },
                            parentAssignedSpeciesListId: subProgram?.assignedSpeciesListId ?? program?.assignedSpeciesListId,
                            ProjectStakeholderRelationship: [],
                            programId: subProgram?.programId ?? program?.programId ?? null,
                            organizationId: program?.organizationId,
                        });
                    }
                );
            }
        } else {
            makePageRequest(
                _ => Promise.all([
                    ProjectService.GetProject(projectId),
                    ContactService.GetProjectContacts(projectId),
                    ConservationActionService.GetConservationActionsForProjectId(projectId),
                    ConservationActionTypeService.Get()
                ]),
                ([project, contacts, conservationActions, types]) => {
                    setProject(project?.result);
                    setRelatedContacts(contacts?.result || []);
                    setChildrenConservationActions(conservationActions?.result || []);
                    setConservationActionTypes(types?.result || []);
                    setEditMode(false);
                },
            );
        }
    };

    const canEdit = () => {
        if(project) {
            if(permission('update_project')) {
                return editContextNodeId(project.contextNodeId);
            }
        }
        return false;
    };

    const canDelete = () => {
        if(project) {
            if(permission('delete_project')) {
                return editContextNodeId(project.contextNodeId);
            }
        }

        return false;
    };

    const enterEditMode = () => {
        setEditMode(true);
        setExistingProject(ObjectUtils.Clone(project));
    };

    const exitEditMode = () => {
        if(existingProject) {
            setEditMode(false);
            setProject(ObjectUtils.Clone(existingProject));
            setExistingProject(null);
        } else {
            changeContext({project: null});
        }
    };

    const updateProject = (project) => {
        setProject(project);
    };

    const saveProject = () => {
        if(project.projectId) {
            makePageRequest(
                _ => ProjectService.UpdateProject(project),
                x => {
                    setEditMode(false);
                    setProject(x.result);
                    validateSlug(x.result);

                    // TODO: update breadcrumbs from redux store
                    // instead of rebuilding context
                    contextBuilder();
                },
            );
        } else {
            makePageRequest(
                () => ProjectService.CreateProject(project),
                (x) => {
                    setProject(x.result);
                    if(x.errorMsg) {
                        setErrorMessage(x.errorMsg);
                    } else {
                        changeContextAndTool(
                            {
                                project: x.result.slug
                            },
                            "summary"
                        );
                    }
                },
            );
        }
    };

    const deleteProject = () => {
        makePageRequest(
            () => ProjectService.DeleteProject(project.projectId),
            (x) => {
                changeContext({project: null});
            },
        );
    };

    const updateValidation = (validation, editorGuid) => {
        //we have multiple model editors on the same object. So let's merge validation keeping any invalid values.
        let target = ObjectUtils.Clone(projectValidation);

        //anything that's now invalid should overwrite a property that was previous valid.
        for(let propertyName in target) {
            if(validation[propertyName]) {
                if(target[propertyName].valid && !validation[propertyName].valid) {
                    target[propertyName] = ObjectUtils.Clone(validation[propertyName]);
                }
            }
        }
        //for anything we don't currently have values about. let's add them.
        for(let propertyName in validation) {
            if(!target[propertyName]) {
                target[propertyName] = ObjectUtils.Clone(validation[propertyName]);
            }
        }

        //noneed to clone. we're working with a fresh copy.
        setProjectValidation(target);
    };

    const isValid = () => {
        for(let propertyName in projectValidation) {
            let validation = projectValidation[propertyName];

            if(!validation.valid) {
                return false;
            }
        }

        return true;
    };

    const conservationActionTypeIsCreatable = (x) => {
        if(!x.isCreatable) {
            //only these types are setup.
            return false;
        }

        //if we have one of this type already it's not creatable.
        if(childrenConservationActions.filter((y) => y.conservationActionTypeId === x.conservationActionTypeId).length) {
            return false;
        }

        return true;
    };

    const validateSlug = (project) => {
        const { match } = props;

        if(project && match && changeContextAndTool) {
            //if the slug doesn't match, we should update the url.
            if(
                match.params.pProgId !== project.grandParentSlug ||
                match.params.cProgId !== project.parentSlug ||
                match.params.projId !== project.slug
            ) {
                changeContextAndTool(
                    {
                        parentProgram: project.grandParentSlug,
                        childProgram: project.parentSlug,
                        project: project.slug,
                        contact: null
                    },
                    match.params.tool
                );
            }
        }
    };

    // don't render page before it is loaded
    if(project === null) {
        return null;
    }

    let creatableConActTypes = conservationActionTypes.filter((x) => conservationActionTypeIsCreatable(x));

    let projectName = project && project.name ? project.name: "Project";

    const latitude = project.Location?.latitude;
    const longitude = project.Location?.longitude;
    const accuracy = project.Location?.accuracy;

    return <Container className={classNames}>
        <Row>
            <ConfirmationDialog
                Title="Warning"
                Body={errorMessage}
                Display={!!errorMessage}
                OnConfirm={() => {
                    setErrorMessage(null);
                    changeContextAndTool(
                        {
                            project: project.slug
                        },
                        "summary"
                    );
                }}
                ConfirmationButtonMessage="OK"
            />
            <ConfirmationDialog
                Title="Confirmation"
                Body="Are you sure you want to delete this project?"
                Display={deleteDialog}
                OnClose={() => {
                    setDeleteDialog(false);
                }}
                OnConfirm={() => {
                    deleteProject();
                }}
            />
            <Col xs={12} md={6}>
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        {
                            editMode ? [
                                    <Button
                                        variant="save"
                                        key={1}
                                        onClick={() => saveProject()}
                                        disabled={!isValid()}
                                    >
                                        Save
                                    </Button>,
                                    <Button
                                        variant="cancel"
                                        key={2}
                                        onClick={() => exitEditMode()}
                                    >
                                        Cancel
                                    </Button>,
                                    projectId !== 'new' && canDelete() ?
                                        <Button
                                            variant="delete"
                                            key={3}
                                            onClick={() => setDeleteDialog(true)}
                                        >
                                            Delete
                                        </Button> :
                                        null
                                ] :
                                (
                                    canEdit() ?
                                        <Button
                                            variant="link"
                                            onClick={() => enterEditMode()}>
                                            <Glyphicon glyph="pencil" />Edit
                                        </Button>:
                                        null
                                )
                        }
                    </div>
                    {projectName}
                </h2>

                {
                    !editMode && project ? [
                            <h3 key="1">Project Summary</h3>,
                            <div className="text-summary pndlm-line-break" key="2">{project.summary}</div>,
                        ] :
                        null
                }

                {
                    project ?
                        <ProjectModelViewer
                            IsNewModel={projectId === "new"}
                            Model={model}
                            EditMode={editMode}
                            Data={project}
                            Update={updateProject}
                            OnValidationUpdate={updateValidation}
                            CustomData={{
                                organizationId: project.organizationId
                            }}
                        />:
                        null
                }
                <ContextNodeBenefitSummarySection contextNodeId={contextNodeId} editMode={editMode} />
            </Col>
            <Col xs={12} md={{span: 5, offset:1}}>
                {
                    project && projectId !== 'new' && creatableConActTypes.length && canEdit() ?
                        <div className="float-right">
                            <Dropdown id={"sb-create-con-act-dropdown"}>
                                <Dropdown.Toggle variant="default" className="float-right h3 btn btn-link">
                                    <Glyphicon glyph="plus-sign" />Add Conservation Action
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {
                                        creatableConActTypes.map((x) =>
                                            <Dropdown.Item
                                                key={x.conservationActionTypeId}
                                                eventKey={x.conservationActionTypeId}
                                                as={StreamBankLink}
                                                Context={
                                                    patchContext({conservationAction: "new"})
                                                }
                                                Tool="summary"
                                                ToolParam={{type: x.conservationActionTypeId}}
                                                AllowNew={true}
                                            >
                                                Add {x.name}
                                            </Dropdown.Item>
                                        )
                                    }
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>:
                        null
                }
                <ChildrenList
                    Title="Conservation Actions"
                    Children={childrenConservationActions}
                    Sort={(a,b) => a.ConservationActionType.name.localeCompare(b.ConservationActionType.name)}
                    Render={(x) => {
                        let editable = editContextNodeId(x.contextNodeId);

                        return <ul className=" unordered-list">
                            <li className={"unordered" + (editable ? " editable" : null)}>
                                <StreamBankLink
                                    className="link-primary"
                                    Context={patchContext({conservationAction: x.conservationActionId})}
                                >
                                    {x.ConservationActionType.name}
                                </StreamBankLink>
                            </li>
                        </ul>;
                    }
                    }
                />
                {
                    project && permission('run_report_project') ?
                    <div className="sb-list-actions">
                        <h2 className="sb-children-list-title">Actions</h2>
                        <ul>
                            <li>
                                <StreamBankLink
                                    className={`link-primary`}
                                    Href={ProjectService.ProjectReportURL(project)}>
                                    <FontAwesomeIcon icon={['fas', 'file']} />
                                    <span style={{marginLeft: 6}}>Generate Report</span>
                                </StreamBankLink>
                            </li>
                        </ul>
                    </div>: null
                }
                {
                    project && permission('view_contacts') ?
                        <ContactList
                            Contacts={relatedContacts}
                            ProjectRoles={project.ProjectStakeholderRelationships}
                            Limit={2}
                        />:
                        null
                }
                {
                    (project && project.projectId && project.Location.latitude && project.Location.longitude) ?
                        <SbMap
                            Lat={latitude}
                            Lng={longitude}
                            Zoom={11}
                            Markers={[
                                {
                                    key: project.projectId,
                                    lat: latitude,
                                    lng: longitude,
                                    accuracy,
                                    marker: !permission('authenticated_user') ? 'circle' : 'both',
                                    content: <div>{project.name}<br />{project.description}</div>
                                }
                            ]}
                        />:
                        null
                }
            </Col>
        </Row>
    </Container>;
};

